import { ReactNode } from "react";
import styled from "styled-components";
import { MainColor, SubColor } from "../../style/styleValue";
import useReveal, { revealStyle } from "./useReveal";

export interface ServiceHighlight {
  title: string;
  desc: string;
}

interface RenewalServiceSectionProps {
  label?: string;
  index?: string;
  title: string;
  subtitle?: string;
  description: ReactNode;
  highlights?: ServiceHighlight[];
  image: string;
  imageAlt?: string;
  imagePosition?: "left" | "right";
  background?: string;
}

const RenewalServiceSection = ({
  label,
  index,
  title,
  subtitle,
  description,
  highlights = [],
  image,
  imageAlt = "Service illustration",
  imagePosition = "left",
  background = "#fff",
}: RenewalServiceSectionProps) => {
  const ref = useReveal<HTMLElement>();

  return (
    <Wrapper ref={ref} $background={background}>
      <div className="container">
        <FlexRow $reverse={imagePosition === "right"}>
          <ImageBox className="reveal">
            <div className="image-frame">
              <img src={image} alt={imageAlt} />
            </div>
          </ImageBox>

          <TextBox className="reveal" style={{ "--reveal-delay": "0.15s" } as React.CSSProperties}>
            {label && (
              <Label>
                {index && <span className="index">{index}</span>}
                {label}
              </Label>
            )}
            <Title>{title}</Title>
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
            <Description>{description}</Description>

            {highlights.length > 0 && (
              <HighlightGrid>
                {highlights.map((item) => (
                  <li key={item.title}>
                    <strong>{item.title}</strong>
                    <span>{item.desc}</span>
                  </li>
                ))}
              </HighlightGrid>
            )}
          </TextBox>
        </FlexRow>
      </div>
    </Wrapper>
  );
};

const Wrapper = styled.section<{ $background: string }>`
  ${revealStyle}
  width: 100%;
  padding: 100px 0;
  background: ${(props) => props.$background};

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  @media (max-width: 768px) {
    padding: 64px 0;
  }
`;

interface FlexRowProps {
  $reverse: boolean;
}

const FlexRow = styled.div<FlexRowProps>`
  display: flex;
  flex-direction: ${(props) => (props.$reverse ? "row-reverse" : "row")};
  align-items: center;
  gap: 72px;

  @media (max-width: 968px) {
    flex-direction: column;
    gap: 40px;
  }
`;

const ImageBox = styled.div`
  flex: 1.1;
  display: flex;
  justify-content: center;
  min-width: 0;

  .image-frame {
    width: 100%;
    max-width: 560px;
    border-radius: 16px;
    overflow: hidden;
    background: #fff;
    border: 1px solid #e8edf3;
    box-shadow: 0 24px 48px rgba(23, 55, 95, 0.1);

    img {
      display: block;
      width: 100%;
      height: auto;
      object-fit: cover;
    }
  }

  @media (max-width: 968px) {
    width: 100%;
    max-width: 600px;
  }
`;

const TextBox = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Label = styled.h3`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  width: fit-content;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${SubColor};
  text-transform: uppercase;
  letter-spacing: 0.16em;
  margin: 0 0 16px;

  .index {
    padding: 4px 10px;
    border-radius: 999px;
    background: ${SubColor}14;
    letter-spacing: 0.05em;
  }
`;

const Title = styled.span`
  font-size: clamp(1.5rem, 2.6vw, 2rem);
  font-weight: 700;
  color: #111;
  line-height: 1.3;
  letter-spacing: -0.03em;
  margin: 0 0 14px;
`;

const Subtitle = styled.span`
  font-size: 1.05rem;
  font-weight: 600;
  color: ${MainColor};
  margin: 0 0 18px;
  word-break: keep-all;
`;

const Description = styled.p`
  font-size: 1rem;
  line-height: 1.85;
  color: #5b6472;
  margin: 0;
  white-space: pre-line; /* 줄바꿈 \n 지원 */
  word-break: keep-all;
`;

const HighlightGrid = styled.ul`
  list-style: none;
  padding: 0;
  margin: 32px 0 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px 24px;

  li {
    position: relative;
    padding-left: 18px;
    display: flex;
    flex-direction: column;
    gap: 4px;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 6px;
      bottom: 4px;
      width: 3px;
      border-radius: 3px;
      background: linear-gradient(${SubColor}, ${SubColor}22);
    }
  }

  strong {
    font-size: 0.95rem;
    font-weight: 700;
    color: #1d2633;
  }

  span {
    font-size: 0.9rem;
    line-height: 1.65;
    color: #6b7482;
    word-break: keep-all;
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

export default RenewalServiceSection;
